import { SENDA } from '@somnhold/content';
import { hexLen, type Hex } from '../hex.ts';
import { idiv, imin } from '../math/fixed.ts';
import { deriveSeed, rngInt, rngRange, seedRng } from '../math/rng.ts';

/**
 * The fort at the foot of the pass (docs/sendas/guerrero.md, chapter 3, «La noche en el fuerte»): its grounds, the same
 * from the seed in the sim and the drawing, integers only. Cells are named by their column in half-hexes (`x2`, so a
 * row's cells are two apart) and their row: `cell(x2, r)`.
 *
 * - `slopeLayout` (act 1, the icy climb): a trail up the slope from the south (+r) to the fort's door at the top, in
 *   switchbacks from the seed; five hexes wide, ice and fallen rock either side (the map's rocks); the landing where
 *   Kel plants his torch, a few rows below the door.
 * - `fortLayout` (acts 2 and 3, the night): the fort in the middle, long from north to south, its gabled roof's ridge
 *   along it; a yard each side under each slope of the roof, walled; the hearth in one of them (the side from the
 *   seed), closed to the outside; the other, the far yard, open to the north where the slope comes up. Between the two
 *   yards a passage one hex wide past the fort's south end, where the nightmares file through to the hearth; the
 *   stable north of the fort, walked through by the hero (not the nightmares' way), with the pile of pieces in it. The
 *   eaves' hooks stand along the fort's walls, three a side.
 * - `roofPlan` (the test): the wind's turns and the pieces from the seed, checked to be playable (`roofCheck`).
 */

/** A cell from its column in half-hexes and its row (the column's parity follows the row's). */
export const cell = (x2: number, r: number): Hex => ({ q: (x2 - r) >> 1, r });
/** A cell's column in half-hexes. */
export const colOf = (h: Hex): number => 2 * h.q + h.r;

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};

export interface SlopeLayout {
  radius: number;
  /** What can't be walked: the ice and rock either side of the trail, the fort above the door. */
  rocks: Hex[];
  /** The trail's middle by row, in half-hexes (index r + R), from the foot (r = R − 1) to the door (r = −(R − 1)). */
  mid: number[];
  /** The way Kel leads the mule: the trail's middle, row by row, from the foot to the door (world points are the caller's). */
  way: { x2: number; r: number }[];
  /** The door (the top of the way), the landing where the train waits (its index), the torch's cell (a row above it). */
  door: { x2: number; r: number };
  landing: number;
  torch: Hex;
  /** Where wolves come down from (cells at the trail's edge), by row. */
  edges: Hex[];
}

export const SLOPE_RADIUS = 9;

/** The icy climb: a trail in switchbacks from the seed, the door at its top, the torch's landing below it. */
export const slopeLayout = (seed: number): SlopeLayout => {
  const R = SLOPE_RADIUS;
  const M = R + 2;
  const rng = seedRng(deriveSeed(seed, 0x51e9));
  // The trail's middle: it leans one way a half-hex a row until four out, then turns (from the seed).
  const mid: number[] = new Array(2 * M + 1).fill(0);
  let c = 0;
  let dir = rngInt(rng, 2) === 0 ? -1 : 1;
  let turn = rngRange(rng, 3, 5);
  for (let r = R - 1; r >= -(R - 1); r--) {
    mid[r + M] = c;
    c += dir;
    if (Math.abs(c) >= turn) {
      dir = -dir;
      turn = rngRange(rng, 3, 5);
    }
  }
  // Beyond the ends the trail runs on straight (the foot below, the fort above).
  for (let r = R; r <= M; r++) mid[r + M] = mid[R - 1 + M] as number;
  for (let r = -M; r <= -R; r++) mid[r + M] = mid[-(R - 1) + M] as number;
  const top = -(R - 1);
  const rocks: Hex[] = [];
  const edges: Hex[] = [];
  for (const h of allCells(M)) {
    const x2 = colOf(h);
    const m = mid[h.r + M] as number;
    // Above the door: the fort (and the slope it sits in).
    const open = h.r >= top && h.r <= R - 1 && Math.abs(x2 - m) <= 5 && hexLen(h.q, h.r) <= R;
    if (!open) rocks.push(h);
    else if (Math.abs(x2 - m) >= 4) edges.push(h);
  }
  const way: { x2: number; r: number }[] = [];
  for (let r = R - 1; r >= top; r--) way.push({ x2: mid[r + M] as number, r });
  const landing = way.length - 1 - SENDA.slope.torch;
  // The torch: a row above the landing, a hex and a half off the way (toward the trail's middle): the troll coming down
  // the way at the train doesn't cross it on its own.
  const l = way[landing + 1] as { x2: number; r: number };
  const tx = l.x2 + (l.x2 > 0 ? -3 : 3);
  const torch = cell(tx + ((tx ^ l.r) & 1), l.r);
  return { radius: R, rocks, mid, way, door: way[way.length - 1] as { x2: number; r: number }, landing, torch, edges };
};

export interface FortLayout {
  radius: number;
  /** What can't be walked: the fort, the stable, the walls, the slope round them. */
  rocks: Hex[];
  /** The hearth's side: −1 west (−x), +1 east. */
  side: number;
  /** The yards by side: [the hearth's, the far one] (walkable cells under each slope of the roof). */
  yards: [Hex[], Hex[]];
  /** The passage between them past the fort's south end, from the far yard to the hearth's. */
  passage: Hex[];
  hearth: Hex;
  /** The pile of pieces, in the stable's walk-through between the two eaves' north ends (`stable`: its cells). */
  pile: Hex;
  stable: Hex[];
  /** Where the hero stands to work each hook: three by each eave, [the hearth's, the far one's], north to south. */
  hooks: [Hex[], Hex[]];
  /** The far yard's mouth to the north, where the nightmares come in, and where they come from up the slope. */
  mouth: Hex;
  spawn: Hex;
  /** The far yard's middle (where a frightened troll runs). */
  farMid: Hex;
  /** The nightmares' way to the hearth: in at the mouth, down the far yard, through the passage. */
  route: Hex[];
  /** The Company's places (the memory): Fermín on the ridge, Juana on the stable's roof, Harl at the door, Sanne and Tomé at the fire. */
  ridge: Hex;
  lookout: Hex;
  door: Hex;
  fire: [Hex, Hex];
}

export const FORT_RADIUS = 8;

/** The fort and its two yards: the hearth's side from the seed. */
export const fortLayout = (seed: number): FortLayout => {
  const R = FORT_RADIUS;
  const M = R + 2;
  const rng = seedRng(deriveSeed(seed, 0xf027));
  const h = rngInt(rng, 2) === 0 ? -1 : 1;
  const f = -h;
  const hearthYard: Hex[] = [];
  const farYard: Hex[] = [];
  const passage: Hex[] = [];
  const walk: Hex[] = [];
  const rocks: Hex[] = [];
  for (const c of allCells(M)) {
    const x2 = colOf(c);
    const r = c.r;
    const inR = hexLen(c.q, c.r) <= R;
    const yard = Math.abs(x2) >= 3 && Math.abs(x2) <= 9 && r >= -4 && r <= 4;
    const pass = r === 3 && Math.abs(x2) <= 1;
    // The stable's walk-through north of the fort: the hero crosses there (the pile is in it); out of both yards.
    const stable = r === -3 && Math.abs(x2) <= 1;
    // The far side's slope above its yard's mouth, where the nightmares come down.
    const approach = r <= -5 && r >= -R && x2 * f >= 2 && x2 * f <= 10;
    if (inR && yard) (x2 * h > 0 ? hearthYard : farYard).push(c);
    else if (inR && pass) passage.push(c);
    else if (inR && stable) walk.push(c);
    else if (!(inR && approach)) rocks.push(c);
  }
  const route = [cell(f * 4, -8), cell(f * 5, -5), cell(f * 6, 2), cell(f * 3, 3), cell(f, 3), cell(h, 3), cell(h * 3, 3)];
  return {
    radius: R,
    rocks,
    side: h,
    yards: [hearthYard, farYard],
    passage: passage.sort((a, b) => colOf(a) * f - colOf(b) * f),
    hearth: cell(h * 6, 2),
    pile: cell(h, -3),
    stable: walk,
    hooks: [
      [cell(h * 4, -2), cell(h * 3, -1), cell(h * 4, 0)],
      [cell(f * 4, -2), cell(f * 3, -1), cell(f * 4, 0)],
    ],
    mouth: cell(f * 5, -5),
    spawn: cell(f * 4, -8),
    farMid: cell(f * 6, 0),
    route,
    ridge: cell(0, 0),
    lookout: cell(f * 2, -4),
    door: cell(h * 3, 1),
    fire: [cell(h * 8, 2), cell(h * 5, 3)],
  };
};

/** The pieces of the roof: the seven shields (the bridge's five, Sanne's and Tomé's), then the sacks of wood. */
export const roofPieces = (sacks: number): { w: number; sack: boolean }[] => [
  ...SENDA.roof.shields.map((w) => ({ w, sack: false })),
  ...Array.from({ length: sacks }, () => ({ w: SENDA.roof.sack, sack: true })),
];

export interface RoofPlan {
  /** How many sacks of wood. */
  sacks: number;
  /** The wind: from `t` (ticks) it loads side `to` (0 the hearth's, 1 the far one), until the next. */
  wind: { t: number; to: number }[];
  /** As the fight starts: where each piece is (0 the hearth's eave, 1 the far one's, −1 the pile), the snow each side. */
  on: number[];
  snow: [number, number];
}

/**
 * The roof's night from the seed (docs/sendas/guerrero.md §4: «Semilla: el lado del hogar, los cambios del viento y
 * cuántos sacos hay»): the wind first loads the hearth's side, with the roof already loaded there (the opening asks for
 * a move within half a minute); then it turns every 60 to 90 s, to the other side two times of three. Kept only if
 * `roofCheck` passes.
 */
export const roofPlan = (seed: number): RoofPlan => {
  for (let k = 0; ; k++) {
    const p = draw(deriveSeed(seed, 0x2007 + k));
    if (roofCheck(p).ok) return p;
  }
};

const draw = (seed: number): RoofPlan => {
  const rng = seedRng(seed);
  const R = SENDA.roof;
  const sacks = rngRange(rng, R.sacks[0], R.sacks[1]);
  const wind: { t: number; to: number }[] = [];
  let to = 0;
  for (let t = 0; t < 12 * 60 * 12; t += rngRange(rng, R.phase[0] / R.wind, R.phase[1] / R.wind) * R.wind) {
    wind.push({ t, to });
    if (rngInt(rng, 3) !== 0) to = 1 - to;
  }
  const pieces = roofPieces(sacks);
  // The opening: the hearth's eave carrying two shields, the far one a little; the rest on the pile.
  const on = pieces.map(() => -1);
  const pick = (want: number, side: number): void => {
    for (let i = 0; i < pieces.length; i++) {
      if (on[i] === -1 && (pieces[i] as { w: number }).w === want) {
        on[i] = side;
        return;
      }
    }
  };
  pick(rngRange(rng, 2, 3), 0);
  pick(2, 0);
  pick(rngRange(rng, 1, 2), 1);
  return { sacks, wind, on, snow: [rngRange(rng, 2, 3), rngRange(rng, 2, 4)] };
};

/** What hangs on each eave. */
export const roofHooks = (pieces: { w: number }[], on: number[]): [number, number] => {
  let a = 0;
  let b = 0;
  on.forEach((s, i) => {
    if (s === 0) a += (pieces[i] as { w: number }).w;
    else if (s === 1) b += (pieces[i] as { w: number }).w;
  });
  return [a, b];
};

/**
 * Checks a night in whole steps of the wind (one of snow every `wind`), integers only:
 *
 * - **the trap**: left as it starts, the roof tips on the hearth before the wind first turns;
 * - **a move is needed soon**: with nothing touched for the first half minute, it tips on the hearth too;
 * - **there is a way**: one piece hung or taken down per step (the eaves holding SENDA.roof.cap each, any weight from 1
 *   to 4 at a time) is enough to keep the hearth's side under the tip the whole night, tipping the far side when it
 *   must (a tip throws all the roof's snow into that yard).
 *
 * It is a matter of timing, not one answer: the document gives the rule, not a single arrangement (docs/DECISIONS.md).
 */
export const roofCheck = (p: RoofPlan, steps = 60): { ok: boolean; trap: boolean; early: boolean; way: boolean } => {
  const R = SENDA.roof;
  const pieces = roofPieces(p.sacks);
  const [h0, f0] = roofHooks(pieces, p.on);
  const lee = (step: number): number => {
    const t = step * R.wind;
    let to = 0;
    for (const w of p.wind) if (w.t <= t) to = w.to;
    return to;
  };
  const firstTurn = p.wind[1]?.t ?? steps * R.wind;
  /** Steps with no move; true if the hearth's side tips before `until` steps. */
  const idle = (until: number): boolean => {
    let sh = p.snow[0];
    let sf = p.snow[1];
    for (let k = 1; k <= until; k++) {
      if (lee(k) === 0) sh++;
      else sf++;
      if (h0 + sh >= R.tip) return true;
      if (f0 + sf >= R.tip) {
        sh = 0;
        sf = 0;
      }
    }
    return false;
  };
  const trap = idle(idiv(firstTurn, R.wind));
  // Half a minute untouched, then the best play from there: still the hearth tips.
  const early = (() => {
    let sh = p.snow[0];
    for (let k = 1; k <= 3; k++) {
      if (lee(k) === 0) sh++;
      if (h0 + sh >= R.tip) return true;
    }
    return false;
  })();
  // Reachable states (hooks each side, snow each side) step by step: one move a step, then the wind, then the tips.
  const key = (a: number, b: number, c: number, d: number): number => ((a * 10 + b) * 16 + c) * 16 + d;
  let cur = new Set<number>([key(h0, f0, p.snow[0], p.snow[1])]);
  for (let k = 1; k <= steps && cur.size; k++) {
    const next = new Set<number>();
    const to = lee(k);
    for (const s of cur) {
      const d = s % 16;
      const c = (s >> 4) % 16;
      const b = idiv(s, 256) % 10;
      const a = idiv(s, 2560);
      for (const [na, nb] of moves(a, b)) {
        let sh = c + (to === 0 ? 1 : 0);
        let sf = d + (to === 1 ? 1 : 0);
        // A hang on the far eave can tip it at once (before the snow comes); else the snow that just came.
        if (nb + sf >= R.tip) {
          sh = 0;
          sf = 0;
        }
        if (na + sh >= R.tip) continue;
        next.add(key(na, nb, imin(15, sh), imin(15, sf)));
      }
    }
    cur = next;
  }
  const way = cur.size > 0;
  return { ok: trap && early && way, trap, early, way };
};

/** One move: a weight of 1 to 4 on or off either eave (or none), within the eaves' hold. */
const moves = (a: number, b: number): [number, number][] => {
  const out: [number, number][] = [[a, b]];
  for (let w = 1; w <= 4; w++) {
    if (a + w <= SENDA.roof.cap) out.push([a + w, b]);
    if (a - w >= 0) out.push([a - w, b]);
    if (b + w <= SENDA.roof.cap) out.push([a, b + w]);
    if (b - w >= 0) out.push([a, b - w]);
  }
  return out;
};

export const HALL_RADIUS = 4;

/**
 * The fort's hall (the scenes inside: the last room, the table, the big hearth): a long room six hexes by four, its
 * walls the map's rocks; the hearth in the north wall, the table down the middle.
 */
export const hallLayout = (): { radius: number; rocks: Hex[] } => {
  const R = HALL_RADIUS;
  const rocks: Hex[] = [];
  for (const c of allCells(R + 2)) if (Math.abs(colOf(c)) > 6 || Math.abs(c.r) > 2) rocks.push(c);
  return { radius: R, rocks };
};
